import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ModalWrap,
    Overlay,
    Container,
    ContentWrapper,
    StickyBar,
    StickyBarStart,
    StickyBarCenter,
    StickyBarEnd,
} from './styles';
import { ModalStickyBarContext } from './ModalStickyBar';
import type { ModalStickySlots } from './ModalStickyBar';
import ExitButton from '@/components/atoms/UI/ExitButton/ExitButton';
import ShadowMount, { getDeepActiveElement } from '@/styles/ShadowMount';
import { InsideShadowReset } from '@/styles/IslandScope';

type ModalMode = 'successPrompt' | 'errorPrompt';

type PropsType = {
    colorful?: boolean
    children: React.ReactNode
    closeModal?: () => void
    unclosed?: boolean
    mode?: ModalMode
}

// Хост может быть Laravel/PHP (узел уже в DOM) или чистый SPA (узла нет).
// Используем существующий #modal-root, иначе создаём один раз — без дублей id.
const ensureModalRoot = (): HTMLElement | null => {
    if (typeof document === 'undefined') return null;
    const existing = document.getElementById('modal-root');
    if (existing) return existing;
    const created = document.createElement('div');
    created.id = 'modal-root';
    document.body.appendChild(created);
    return created;
};

/**
 * Все фокусопригодные потомки внутри контейнера модалки. Используется
 * для focus-trap (Tab/Shift+Tab «закольцовываем» внутри модалки) и для
 * автофокуса при открытии, если кнопки закрытия нет (`unclosed`).
 *
 * Селекторы стандартные («interactive elements that take focus by
 * default»), `[hidden]` отсеиваем — `inert` нативно убирает узел из
 * tab-order. Закрытые `<details>` оставляем: их `<summary>` сам по
 * себе фокусируется.
 */
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',');

const Modal: React.FC<PropsType> = (props) => {
    const {children, closeModal, unclosed, mode, colorful} = props;
    const modalRoot = useMemo(ensureModalRoot, []);
    const { t } = useTranslation();
    const text_close = t('modal.close');
    const containerRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    /* Узлы зон — ref-коллбэком в state: нужен повторный рендер, чтобы порталы слотов встали в шапку. */
    const [stickyStart, setStickyStart] = useState<HTMLDivElement | null>(null);
    const [stickyCenter, setStickyCenter] = useState<HTMLDivElement | null>(null);
    const stickySlots = useMemo<ModalStickySlots | null>(
        () => (unclosed ? null : { start: stickyStart, center: stickyCenter }),
        [unclosed, stickyStart, stickyCenter],
    );

    /**
     * Автозакрытие промпт-модалок (success/error) спустя 2с. История —
     * `closeModal` стабилен в текущей сигнатуре `useModal`, но завязка
     * на reference этого пропа была и до изменений.
     */
    useEffect(() => {
        if (mode === 'successPrompt' || mode === 'errorPrompt') {
            const timeoutId = setTimeout(() => {
                if (closeModal) {
                    closeModal();
                }
            }, 2000);

            return () => clearTimeout(timeoutId);
        }
    }, [mode, closeModal]);

    /**
     * Перевод фокуса в модалку при открытии + возврат туда, откуда
     * фокус был, при закрытии. Без этого клавиатурная навигация
     * остаётся «под модалкой»: Tab уходит в скрытый под оверлеем
     * контент, что нарушает базовую a11y и заметно глазами (пользователь
     * жмёт Tab и кнопки в модалке не подсвечиваются).
     *
     * Фокусируем сам диалог-контейнер (role="dialog", tabindex=-1), а НЕ
     * крестик: программный `.focus()` на `<button>` внутри Shadow DOM на
     * iOS Safari зажигает `:focus-visible` (видимое кольцо вокруг крестика),
     * хотя пользователь открыл модалку тапом, а не клавиатурой. Фокус на
     * контейнере уводит фокус-ловушку внутрь модалки и озвучивается screen
     * reader-ом (это и есть ARIA-паттерн initial focus для диалога), но без
     * кольца. Клавиатурой пользователь дотабается до крестика — и получит
     * кольцо штатно (`:focus-visible` от реального Tab).
     *
     * Возврат фокуса делаем на `unmount`, не на `closeModal()` —
     * `useModal` размонтирует `<Modal>` синхронно с `hide()`, до того
     * как React успеет выполнить onClick-эффекты потомков. Цепляться к
     * `closeModal` бессмысленно: его reference меняется на каждый
     * render консумера.
     *
     * `getDeepActiveElement` нужен из-за Shadow DOM: открывшая модалку
     * кнопка живёт внутри shadow острова, а `document.activeElement`
     * возвращает shadow host, а не реальный focused-узел. Без обхода
     * shadow-границ возврат фокуса работает на shadow host, что
     * визуально равносильно «фокус потерян».
     */
    useEffect(() => {
        if (typeof document === 'undefined') return;
        previouslyFocusedRef.current = getDeepActiveElement() as HTMLElement | null;

        // Микрозадержка нужна, потому что портал createPortal вставляет
        // узлы в DOM до того, как браузер обновит rendering — без неё
        // .focus() иногда работает, но визуально outline появляется на
        // следующем кадре, что выглядит как «дёрганье».
        //
        // preventScroll: true ОБЯЗАТЕЛЕН. Container — scroll-контейнер
        // (overflow: auto) и в момент фокуса ещё сдвинут входной анимацией
        // (translateY). Без флага браузер доскролливает его «в вид» к
        // layout-позиции → модалка рывком уезжает вверх за экран и резко
        // встаёт на место. Фокус нам нужен только для фокус-ловушки/SR,
        // скроллить ничего не надо.
        const id = window.requestAnimationFrame(() => {
            containerRef.current?.focus({ preventScroll: true });
        });

        return () => {
            window.cancelAnimationFrame(id);
            previouslyFocusedRef.current?.focus?.();

            // Гард от click/hover-through. Модалка размонтируется синхронно, и
            // точка тапа/курсора над крестиком оказывается над элементом
            // страницы под ним (напр. .top-navigation__account — аватар с
            // CSS-hover-меню в правом верхнем углу, ровно под крестиком).
            // iOS на тап генерит синтетические mouse-события на элемент под
            // точкой, а CSS :hover активируется тапом → меню ghost-открывается.
            // :hover нельзя отменить JS-событием (это стиль, не cancelable
            // event) — гасит только pointer-events. На короткое окно делаем
            // страницу неинтерактивной: синтетический ghost (срабатывает
            // практически сразу, в рамках того же жеста) до неё не достаёт.
            // По таймеру (не по pointer-событиям: иначе первый осознанный тап
            // пользователя пришёлся бы на ещё-none body и был бы потерян).
            const { body } = document;
            const prevPointerEvents = body.style.pointerEvents;
            body.style.pointerEvents = 'none';
            window.setTimeout(() => {
                body.style.pointerEvents = prevPointerEvents;
            }, 300);
        };
    }, []);

    /**
     * Esc — стандартный шорткат закрытия модалки. Уважаем `unclosed`
     * (например, обязательные подтверждения), там Esc игнорируется.
     */
    useEffect(() => {
        if (unclosed) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && closeModal) {
                e.stopPropagation();
                closeModal();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [unclosed, closeModal]);

    /**
     * Focus-trap: Tab на последнем focusable перепрыгивает на первый,
     * Shift+Tab на первом — на последний. Так фокус не «вытекает» в
     * скрытую под оверлеем страницу и базовая клавиатурная навигация
     * замкнута внутри модалки.
     *
     * `getDeepActiveElement` нужен по той же причине, что и в эффекте
     * выше — модалка живёт в Shadow DOM на `#modal-root`, и обычный
     * `document.activeElement` возвращает shadow host.
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Tab') return;
        const container = containerRef.current;
        if (!container) return;
        const focusables = Array.from(
            container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => !el.hasAttribute('inert'));
        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = getDeepActiveElement();

        if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
            return;
        }
        if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
        }
    }, []);

    if (!modalRoot) return null;

    /*
     * Модалка изолируется в Shadow DOM на `#modal-root`. Это покрывает
     * оба сценария: useModal-инлайн (любая страница вызывает
     * `useModal()` и рендерит <Modal/>) и event-bus через ModalHost.
     * `ShadowMount` лениво вешает `attachShadow` на хост и порталит
     * содержимое внутрь — Bootstrap / normalize не достают до контента
     * модалки, и наоборот.
     */
    return (
        <ShadowMount host={modalRoot}>
            <InsideShadowReset />
            <ModalWrap role="presentation">
                <Overlay onClick={unclosed ? () => null : closeModal}/>
                <Container
                    $colorful={colorful}
                    $mode={mode}
                    ref={containerRef}
                    onKeyDown={handleKeyDown}
                    role="dialog"
                    aria-modal="true"
                    tabIndex={-1}
                >
                    {!unclosed &&
                        <StickyBar>
                            <StickyBarStart ref={setStickyStart} />
                            <StickyBarCenter ref={setStickyCenter} />
                            <StickyBarEnd>
                                <ExitButton
                                    ref={closeButtonRef}
                                    onClick={closeModal}
                                    areaSize={7}
                                    iconSize={2.5}
                                    aria-label={text_close}
                                />
                            </StickyBarEnd>
                        </StickyBar>
                    }

                    <ModalStickyBarContext.Provider value={stickySlots}>
                        <ContentWrapper>{children}</ContentWrapper>
                    </ModalStickyBarContext.Provider>
                </Container>
            </ModalWrap>
        </ShadowMount>
    )
}

export default Modal
